/**
 * PreferencesContent - User preferences settings panel
 * 
 * Allows users to configure theme, notifications, and other preferences.
 */

import { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button, Label } from '@/components/ui';

interface PreferencesContentProps {
  // Future: notification settings from backend
}

type _ThemeSetting = 'light' | 'dark' | 'auto';

export function PreferencesContent(_props: PreferencesContentProps) {
  const { theme, setTheme } = useTheme();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailDigest, setEmailDigest] = useState<'daily' | 'weekly' | 'never'>('weekly');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const handleSavePreferences = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      // Theme is already saved by ThemeContext
      // TODO: Save notification preferences to backend
      localStorage.setItem('userPreferences', JSON.stringify({
        notificationsEnabled,
        emailDigest,
        soundEnabled,
      }));
      setMessage({ type: 'success', text: 'Preferences saved successfully' });
    } catch (_error) {
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header - matches DocumentViewerContent styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <h2 className="text-sm font-medium text-foreground flex-1">
          Preferences
        </h2>
      </div>
      
      {/* Content - matches DocumentViewerContent styling */}
      <div className="flex-1 overflow-y-auto p-4 bg-card space-y-4">
        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-success-light border border-success text-success'
              : 'bg-destructive-light border border-destructive text-destructive'
          }`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}
        
        {/* Appearance Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Appearance
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-medium text-foreground mb-2">
                Theme
              </Label>
              <div className="flex gap-2">
                <ThemeButton
                  label="Light"
                  icon="☀️"
                  isSelected={theme === 'light'}
                  onClick={() => setTheme('light')}
                />
                <ThemeButton
                  label="Dark"
                  icon="🌙"
                  isSelected={theme === 'dark'}
                  onClick={() => setTheme('dark')}
                />
                <ThemeButton
                  label="System"
                  icon="🔄"
                  isSelected={theme === 'auto'}
                  onClick={() => setTheme('auto')}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {theme === 'auto' 
                  ? 'Theme will match your system preference'
                  : `Using ${theme} theme`
                }
              </p>
            </div>
          </div>
        </div>
        
        {/* Notifications Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Notifications
          </h3>
          
          <div className="space-y-4">
            {/* In-app notifications */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  In-App Notifications
                </p>
                <p className="text-xs text-muted-foreground">
                  Show notifications within the application
                </p>
              </div>
              <ToggleSwitch
                enabled={notificationsEnabled}
                onChange={setNotificationsEnabled}
              />
            </div>
            
            {/* Sound */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Sound Effects
                </p>
                <p className="text-xs text-muted-foreground">
                  Play sounds for notifications
                </p>
              </div>
              <ToggleSwitch
                enabled={soundEnabled}
                onChange={setSoundEnabled}
              />
            </div>
            
            {/* Email digest */}
            <div>
              <Label className="block text-sm font-medium text-foreground mb-2">
                Email Digest
              </Label>
              {/* eslint-disable-next-line no-restricted-syntax -- Native select for email digest dropdown */}
              <select
                value={emailDigest}
                onChange={(e) => setEmailDigest(e.target.value as 'daily' | 'weekly' | 'never')}
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="daily">Daily summary</option>
                <option value="weekly">Weekly summary</option>
                <option value="never">Never</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Keyboard Shortcuts */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Keyboard Shortcuts
          </h3>
          
          <div className="space-y-2">
            <ShortcutRow keys={['⌘', 'P']} description="Open project switcher" />
            <ShortcutRow keys={['⌘', 'K']} description="Open command palette" />
            <ShortcutRow keys={['⌘', 'Enter']} description="Send message" />
            <ShortcutRow keys={['Esc']} description="Close modal / Cancel" />
            <ShortcutRow keys={['⌘', 'S']} description="Save document" />
          </div>
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSavePreferences}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Theme button component
interface ThemeButtonProps {
  label: string;
  icon: string;
  isSelected: boolean;
  onClick: () => void;
}

function ThemeButton({ label, icon, isSelected, onClick }: ThemeButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="outline"
      className={`flex-1 h-auto py-3 flex-col ${
        isSelected
          ? 'border-primary bg-info-light text-info'
          : ''
      }`}
    >
      <div className="text-xl mb-1">{icon}</div>
      <div className="text-sm font-medium">{label}</div>
    </Button>
  );
}

// Toggle switch component
interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function ToggleSwitch({ enabled, onChange }: ToggleSwitchProps) {
  return (
    <Button
      onClick={() => onChange(!enabled)}
      variant="ghost"
      className={`relative w-11 h-6 rounded-full p-0 ${
        enabled ? 'bg-primary' : 'bg-secondary'
      }`}
    >
      <div
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-card rounded-full shadow transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </Button>
  );
}

// Shortcut row component
interface ShortcutRowProps {
  keys: string[];
  description: string;
}

function ShortcutRow({ keys, description }: ShortcutRowProps) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-foreground">{description}</span>
      <div className="flex gap-1">
        {keys.map((key, idx) => (
          <kbd
            key={idx}
            className="px-2 py-1 bg-muted/50 rounded text-xs font-mono text-foreground"
          >
            {key}
          </kbd>
        ))}
      </div>
    </div>
  );
}
